import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import mapValues from 'lodash/mapValues'
import padStart from 'lodash/padStart'
import sortBy from 'lodash/sortBy'
import React, { FC, useCallback, useContext, useMemo, useState } from 'react'

import SvgChangeAddress from '@Assets/SVG/ChangeAddress'
import SvgEdit from '@Assets/SVG/Edit'
import { Button } from '@Components/Button'
import { LoadingSpinner } from '@Components/Loading'
import { NativeModal, NativeModalBody, NativeModalHeader, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { Button as ButtonCore } from '@CoreComponents/Buttons/Button'
import { ShippingLogo } from '@Domains/Checkout/Steps/Delivery/Components/DeliveryLogo'
import { CheckoutFlowContext } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/CheckoutFlowContext'
import { useCheckoutFieldsValidations } from '@Domains/CheckoutV2/Features/CheckoutSteps/Hooks/UseCheckoutFlowStepValidation'
import {
  DeliveryDropPointItem,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutShippingStep/Components/DeliveryDropPointItem/DeliveryDropPointItem'
import {
  DeliveryDropPointMap,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutShippingStep/Components/DeliveryDropPointMap/DeliveryDropPointMap'
import { useSetAddressesInCartMutation, useShippingMethodQuery } from '@Generated/graphql'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { CheckoutCartV2BillingAddress, CheckoutCartV2ShippingMethod, CheckoutV2ServiceLocation } from '@Pages/onestepcheckout'
import { TextInput, useTextInputActionRef } from '@Shared/Components/Inputs/TextInput/TextInput'
import { CapitalizeFirstLetter } from '@Shared/Utilities/StringHelpers/CapitalizeFirstLetter'

import styles from './DeliveryDropPointDialog.module.scss'

const pad = (num: string) => padStart(num, 2, '0')

export const useFormatOpeningHours = () => {
  const { t } = useTranslation('checkout')
  const formatOpeningHours = useCallback((hour) => {
    const timePart = `${pad(hour.opens?.hours)}:${pad(hour.opens?.minutes)} - ${pad(hour.closes?.hours)}:${pad(hour.closes?.minutes)}`
    if (!hour?.dayOfWeek) {
      return timePart
    }

    const dayOfWeek = CapitalizeFirstLetter(t(`checkout:COF_${hour.dayOfWeek.toUpperCase()}`, hour.dayOfWeek).toString())
    return `${dayOfWeek}: ${timePart}`
  }, [t])
  return { formatOpeningHours }
}
export type DeliveryDropPointAddress =
  Pick<CheckoutCartV2BillingAddress, 'streetName'>
  & Pick<CheckoutCartV2BillingAddress, 'postalCode'>
  & Pick<CheckoutCartV2BillingAddress, 'city'>

const replaceObjectWhere = (
  obj,
  replaceValuesEq: Array<string | null | undefined>,
  to: string | number | null | undefined,
) => mapValues(obj, v => (replaceValuesEq?.includes(v) ? to : v))

export interface DeliveryDropPointDialogProps {
  countryCode?: string | null
  onClose: () => void
  onSelect: (shippingMethod: CheckoutCartV2ShippingMethod, serviceLocation: CheckoutV2ServiceLocation) => void
  selectedServiceLocationId?: string
  serviceLocations?: CheckoutV2ServiceLocation[]
  shippingMethod: CheckoutCartV2ShippingMethod
  zipCodes: Record<string, string>
}

const useAddressRefs = () => {
  const streetName = useTextInputActionRef()
  const city = useTextInputActionRef()
  const postalCode = useTextInputActionRef()
  return {
    city,
    streetName,
    postalCode,
  }
}

export const DeliveryDropPointDialog: FC<DeliveryDropPointDialogProps> = ({
  countryCode,
  onClose,
  onSelect,
  selectedServiceLocationId,
  serviceLocations,
  shippingMethod,
  zipCodes,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const actionRef = useNativeModalActions()
  const addressActionRef = useNativeModalActions()
  const { formatOpeningHours } = useFormatOpeningHours()
  const [editMode, setEditMode] = useState(false)
  const [enableSpinner, setEnableSpinner] = useState(false)
  const {
    model: {
      billingAddress,
      shippingAddress,
    },
    updateStep,
  } = useContext(CheckoutFlowContext)
  const [saveAddressMutation] = useSetAddressesInCartMutation({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  const address = useMemo(
    () => (shippingAddress.city && shippingAddress.postalCode && shippingAddress.streetName
      ? shippingAddress
      : billingAddress),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const [tempAddress, setTempAddress] = useState(address)

  const {
    data,
    loading: serviceLocationsLoading,
  } = useShippingMethodQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
    variables: {
      shippingMethodId: shippingMethod.id,
      postalCode: address.postalCode,
      streetName: address.streetName,
      country: address?.country || countryCode,
      city: address.city,
    },
  })
  const saveTempAddress = useCallback(() => {
    updateStep('shippingAddress', { ...tempAddress })
    updateStep('showShippingAddress', { checked: true })
    saveAddressMutation({
      variables: {
        billingAddress: replaceObjectWhere(billingAddress, ['', null], undefined),
        shippingAddress: replaceObjectWhere(shippingAddress, ['', null], undefined),
      },
    })
  }, [billingAddress, saveAddressMutation, shippingAddress, tempAddress, updateStep])
  const { getValidations } = useCheckoutFieldsValidations()
  const validations = getValidations(countryCode)
  const actionRefs = useAddressRefs()
  const locations = data?.shippingMethod?.serviceLocations || serviceLocations || []
  const loading = !data ? true : enableSpinner && serviceLocationsLoading

  const handleEditMode = useCallback(() => {
    if (cofDesignV1) {
      addressActionRef.current?.open()
    } else {
      setEditMode(true)
    }
  }, [addressActionRef, cofDesignV1])

  if (!shippingMethod) {
    return null
  }
  return (
    <>
      <NativeModal
        actionRef={actionRef}
        backdrop
        className={clsx(styles.deliveryDropPointDialog, cofDesignV1 && styles.cofDesignV1)}
        closeOnBackDrop
        closeOnEscape
        defaultOpen
        onClose={onClose}
      >
        <NativeModalHeader>
          <ShippingLogo className={styles.headerLogo} deliveryType={shippingMethod.title || ''} url={shippingMethod.logo?.url} />
        </NativeModalHeader>
        <NativeModalBody>
          <div className={styles.deliveryDropPoint}>
            {!editMode && (
              <div className={clsx(styles.address, styles.show)}>
                <button onClick={handleEditMode} type="button">
                  <strong>{t('checkout:FILL_OUT_YOUR_ADDRESS')}</strong>
                  {cofDesignV1 && <SvgChangeAddress />}

                  {cofDesignV1
                    ? (
                      <p>
                        {`
                      ${shippingAddress.streetName || billingAddress.streetName},
                      ${shippingAddress.postalCode || billingAddress.postalCode},
                      ${shippingAddress.city || billingAddress.city}`}
                      </p>
                    )
                    : (
                      <>
                        <p>{shippingAddress.streetName || billingAddress.streetName}</p>
                        <p>{`${shippingAddress.postalCode || billingAddress.postalCode}, ${shippingAddress.city || billingAddress.city}`}</p>
                      </>
                    )}
                  {!cofDesignV1 && <SvgEdit />}
                </button>
              </div>
            )}
            {!cofDesignV1 && editMode && (
              <div className={clsx(styles.address, styles.edit)}>
                <strong>{t('checkout:FILL_OUT_YOUR_ADDRESS')}</strong>
                <TextInput
                  actionRef={actionRefs.streetName}
                  className={styles.addressInput}
                  label={`${t('checkout:COF_ADDRESS_STREET_AND_NUMBER')} *`}
                  onChange={value => setTempAddress(prev => ({
                    ...prev,
                    streetName: value,
                  }))}
                  validations={validations.streetName}
                  value={tempAddress.streetName || ''}
                />
                <TextInput
                  actionRef={actionRefs.postalCode}
                  className={styles.zipcodeInput}
                  label={`${t('checkout:COF_POSTCODE')} *`}
                  onChange={(value) => {
                    if (zipCodes[value]) {
                      setTempAddress(prev => ({
                        ...prev,
                        postalCode: value,
                        city: zipCodes[value],
                      }))
                      actionRefs.city.current?.validate()
                      return
                    }
                    setTempAddress(prev => ({
                      ...prev,
                      postalCode: value,
                    }))
                  }}
                  validations={validations.postalCode}
                  value={tempAddress.postalCode || ''}
                />
                <TextInput
                  actionRef={actionRefs.city}
                  className={styles.cityInput}
                  label={`${t('checkout:COF_CITY')} *`}
                  onChange={value => setTempAddress(prev => ({
                    ...prev,
                    city: value,
                  }))}
                  validations={validations.city}
                  value={tempAddress.city || ''}
                />
                <span className={styles.buttons}>
                  <Button
                    className={styles.deny}
                    color="grey"
                    onClick={() => {
                      setEditMode(false)
                      setEnableSpinner(true)
                      setTempAddress(shippingAddress)
                    }}
                  >
                    {t('checkout:COF_CANCEL')}
                  </Button>
                  <Button
                    className={styles.apply}
                    onClick={() => {
                      saveTempAddress()
                      setEditMode(false)
                    }}
                  >
                    {t('checkout:UPDATE_LABEL')}
                  </Button>
                </span>
              </div>
            )}
            {loading && <div className={styles.loading}><LoadingSpinner /></div>}
            {!loading && isEmpty(locations) && <strong className={styles.noDropPoints}>{t('checkout:UNABLE_TO_FIND_PICKUP_LOCATIONS_TEXT')}</strong>}
            {!loading && !isEmpty(locations) && (
              <>
                <div className={styles.options}>
                  {map(sortBy(locations, serviceLocation => (serviceLocation.externalID === selectedServiceLocationId ? -1 : 1)), serviceLocation => (
                    <DeliveryDropPointItem
                      key={serviceLocation.externalID}
                      city={serviceLocation.city || ''}
                      distance={serviceLocation.distance || 0}
                      handleDropPointSelection={(dropPointId) => {
                        onSelect(shippingMethod, locations.find(a => a.externalID === dropPointId)!)
                        actionRef.current?.close()
                      }}
                      id={serviceLocation.externalID!}
                      name={serviceLocation.name || ''}
                      openingHours={(serviceLocation.openingHours || [])?.map(formatOpeningHours)}
                      postalCode={serviceLocation.postalCode || ''}
                      selectedDropPointId={selectedServiceLocationId || undefined}
                      streetName={serviceLocation.streetName || ''}
                    />
                  ))}
                </div>
                <div className={styles.map}>
                  <DeliveryDropPointMap
                    address={shippingAddress}
                    onMarkerClick={(point) => {
                      actionRef.current?.close()
                      onSelect(shippingMethod, locations.find(a => a.externalID === point.externalID)!)
                    }}
                    selectedDropPointId={selectedServiceLocationId}
                    serviceLocations={locations}
                  />
                </div>
              </>
            )}
          </div>
        </NativeModalBody>
      </NativeModal>
      {cofDesignV1 && (
        <NativeModal actionRef={addressActionRef} className={styles.addressModal} closeOnBackDrop>
          <NativeModalHeader className={styles.addressModalHeader}>
            {t('checkout:FILL_OUT_YOUR_ADDRESS')}
          </NativeModalHeader>
          <NativeModalBody className={styles.addressModalBody}>
            <div className={styles.addressModalInner}>

              <strong>{t('checkout:COF_DELIVERY_ADDRESS')}</strong>
              <div className={styles.addressModalFields}>
                <TextInput
                  actionRef={actionRefs.streetName}
                  className={styles.addressInput}
                  label={`${t('checkout:COF_ADDRESS_STREET_AND_NUMBER')} *`}
                  onChange={value => setTempAddress(prev => ({
                    ...prev,
                    streetName: value,
                  }))}
                  validations={validations.streetName}
                  value={tempAddress.streetName || ''}
                />
                <TextInput
                  actionRef={actionRefs.postalCode}
                  className={styles.zipcodeInput}
                  label={`${t('checkout:COF_POSTCODE')} *`}
                  onChange={(value) => {
                    if (zipCodes[value]) {
                      setTempAddress(prev => ({
                        ...prev,
                        postalCode: value,
                        city: zipCodes[value],
                      }))
                      actionRefs.city.current?.validate()
                      return
                    }
                    setTempAddress(prev => ({
                      ...prev,
                      postalCode: value,
                    }))
                  }}
                  validations={validations.postalCode}
                  value={tempAddress.postalCode || ''}
                />
                <TextInput
                  actionRef={actionRefs.city}
                  className={styles.cityInput}
                  label={`${t('checkout:COF_CITY')} *`}
                  onChange={value => setTempAddress(prev => ({
                    ...prev,
                    city: value,
                  }))}
                  validations={validations.city}
                  value={tempAddress.city || ''}
                />
              </div>
              <ButtonCore
                className={styles.apply}
                innerSpacing="medium"
                onClick={() => {
                  saveTempAddress()
                  addressActionRef.current?.close()
                }}
                theme="black"
              >
                {t('checkout:UPDATE_LABEL')}
              </ButtonCore>
            </div>
          </NativeModalBody>
        </NativeModal>
      )}
    </>
  )
}

